<template>
  <UIModal v-model="isOpen" :front-blur="innerModalOpen" disable-content-overflow-auto max-height>
    <template #header>
      <div class="tw:flex tw:flex-col tw:items-center tw:gap-4">
        <div class="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:mx-auto tw:max-w-2xl">
          <div class="tw:flex tw:flex-col tw:items-center">
            <h2 class="tw:font-bold tw:text-white tw:text-3xl">{{ $t('warehouse') }}</h2>
            <p class="tw:text-[#88E1FF] tw:text-sm tw:md:text-base tw:text-center tw:leading-5">
              {{ $t(props.description || '') }}
            </p>
          </div>
          <div class="tw:mx-auto tw:border tw:border-[#1E353E] tw:rounded-xl tw:w-full tw:overflow-hidden">
            <UITabs v-model="selectedTab" :tabs="tabs" size="sm" />
          </div>
        </div>
        <TokenSearchBar
          v-model="search"
          v-model:with-balance-only="withBalanceOnly"
          :hide-balance-check="selectedTab === TOKEN_TAB.NFTs"
          class="tw:w-full"
        />
      </div>
    </template>
    <template #default>
      <StorageDesktop
        :loading="isLoadingItems || loadingNft"
        :nft-tokens="nftTokens"
        :search
        :token-tab="selectedTab"
        :tokens="storageItems"
        :with-balance-only="withBalanceOnly"
        class="tw:lg:min-w-4xl"
        @reset="handleReset"
        @inner-modal-open="innerModalOpen = true"
        @inner-modal-close="innerModalOpen = false"
      />
    </template>
  </UIModal>
</template>

<script lang="ts">
export enum TOKEN_TAB {
  ALL = 'all',
  MINED = 'mined',
  CRAFTED = 'crafted',
  NFTs = 'nfts'
}
</script>

<script lang="ts" setup>
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { BigNumber } from 'bignumber.js';
import { formatEther } from 'ethers';
import {
  Erc20Token__factory,
  ERC721__factory,
  Mine__factory,
  Multicall__factory,
  PearlYield__factory
} from 'public/abi/evm/types';
import type { ITokenModel, StorageItem } from '~/types/apiService';
import type { ContractAddresses, ContractAddressesItem } from '~/types/contractAddresses';
import type { TokenDescription } from '~/types/token';
import { apiUrls, tokensConfig } from '~/utils/constants';
import TokenSearchBar from '~/widgets/wallet/token-search/ui/TokenSearchBar.vue';

type TokenMap = Record<string, string>;
type NftMetadata = { name: string; image_url: string };

const props = defineProps<{
  isStorageSelected: boolean;
  description: string;
}>();

const innerModalOpen = ref(false);

const { t } = useI18n();
const { apiUrl, blockchain } = useEnvs();
const { address } = useWeb3ModalAccount();
const { getMulticallResult } = useAbiAccess();
const { getProvider } = useProvider();
const storageItems = ref<StorageItem[]>([]);
const nftTokens = ref<TokenDescription[]>([]);
const loadingNft = ref(false);

const search = ref<string>('');
const withBalanceOnly = ref<boolean>(false);
const selectedTab = ref<TOKEN_TAB>(TOKEN_TAB.ALL);
const tabs = computed(() => [
  { label: t('walletTabAll'), value: TOKEN_TAB.ALL },
  { label: t('walletTabMined'), value: TOKEN_TAB.MINED },
  { label: t('walletTabCrafted'), value: TOKEN_TAB.CRAFTED },
  { label: 'NFT', value: TOKEN_TAB.NFTs }
]);

function handleReset() {
  search.value = '';
  withBalanceOnly.value = false;
  selectedTab.value = TOKEN_TAB.ALL;
}

const { data: tokensData, refresh: refreshTokens } = useFetch<ITokenModel[]>(apiUrls.token.tokens, {
  baseURL: apiUrl,
  transform: (data) => {
    return data.sort((a, b) => a.displayOrder - b.displayOrder);
  }
});

const isOpen = computed(() => !!(props.isStorageSelected && tokensData?.value));
const nonNftTokens = computed(() => tokensData.value && tokensData.value.filter((token) => !token.isNft));

function getErc20Sellables(): [ITokenModel[], string[]] {
  if (!nonNftTokens.value) return [[], []];

  const erc20Sellable = nonNftTokens.value.filter((token) => token.isSellable);
  const erc20sellableAddresses = erc20Sellable.map((token) => token.tokenContractAddress);

  return [erc20Sellable, erc20sellableAddresses];
}

async function getTokenBalances(nonNftTokens: ITokenModel[]): Promise<BigNumber[]> {
  const erc20Addresses = nonNftTokens.map((token: ITokenModel) => token.tokenContractAddress);
  const multicallBalanceOfResult = await getMulticallResult(
    Erc20Token__factory.createInterface(),
    erc20Addresses,
    'balanceOf',
    true
  );

  return multicallBalanceOfResult.map((multicallElem: [boolean, string]) => {
    return new BigNumber(multicallElem[1]).shiftedBy(-18).dp(18);
  });
}

async function getShared(): Promise<TokenMap> {
  const [erc20Sellable, erc20sellableAddresses] = getErc20Sellables();

  const stakeAddresses = erc20Sellable.map((erc20Token: ITokenModel) => {
    const tokenAddresses = blockchain.contracts[erc20Token.key]?.addresses;

    return tokenAddresses.mine ? tokenAddresses.mine : tokenAddresses.yield;
  });

  const multicallTotalStakeResult = await getMulticallResult(
    Mine__factory.createInterface(),
    stakeAddresses,
    'totalSupply'
  );
  const totalStakes = multicallTotalStakeResult.map((multicallElem: [boolean, string]) => {
    const stake = formatEther(BigInt(multicallElem[1]));

    return BigNumber(Number(stake));
  });

  const multicallTokenTotalStakesResult = await getMulticallResult(
    Mine__factory.createInterface(),
    stakeAddresses,
    'balanceOf',
    true
  );
  const shares = multicallTokenTotalStakesResult.map((multicallElem: [boolean, string], index: number) => {
    const tokenTotalStake = formatEther(BigInt(multicallElem[1]));

    const share = totalStakes[index].isZero()
      ? '0'
      : BigNumber(Number(tokenTotalStake)).dividedBy(totalStakes[index]).multipliedBy(100).toFixed(4);

    return {
      [erc20sellableAddresses[index]]: share
    };
  });
  const sharesHashMap: TokenMap = Object.assign({}, ...shares);

  return sharesHashMap;
}

async function getEarnedBalance(): Promise<TokenMap> {
  const mineInterface = Mine__factory.createInterface();
  const pearlYieldInterface = PearlYield__factory.createInterface();
  const [erc20Sellable, erc20sellableAddresses] = getErc20Sellables();
  const multicallContract = Multicall__factory.connect(blockchain.multicallAddress, getProvider());
  const multicallData = erc20Sellable.map((token: ITokenModel) => {
    const isLicenseToken = blockchain.contracts[token.key]?.addresses?.mine;
    const calldata = isLicenseToken
      ? mineInterface.encodeFunctionData('mined', [String(address.value)])
      : pearlYieldInterface.encodeFunctionData('earned', [String(address.value)]);

    const multicallAddress = isLicenseToken ? isLicenseToken : blockchain.contracts[token.key]?.addresses?.yield;
    return [multicallAddress, calldata];
  });

  const multicallTotalEarnedResult = await multicallContract?.tryAggregate.staticCall(
    true,
    multicallData.map((item) => ({
      target: item[0],
      callData: item[1]
    }))
  );
  const totalEarned = multicallTotalEarnedResult.map((multicallElem: [boolean, string], index: number) => {
    const isLicenseToken = blockchain.contracts[erc20Sellable[index].key]?.addresses?.mine;

    const earnedBalance = isLicenseToken
      ? BigNumber(formatEther(mineInterface.decodeFunctionResult('mined', multicallElem[1])[0]))
      : BigNumber(Number(formatEther(BigInt(multicallElem[1]))));
    return {
      [erc20sellableAddresses[index]]: earnedBalance
    };
  });
  const totalEarnedHashMap: TokenMap = Object.assign({}, ...totalEarned);

  return totalEarnedHashMap;
}

const isLoadingItems = ref(false);

const storageItemsEventBus = useEventBus<void>('storage-items');

const busListener = () => {
  getStorageItems();
};
storageItemsEventBus.on(busListener);

onUnmounted(() => {
  storageItemsEventBus.off(busListener);
});

async function getStorageItems() {
  if (!address.value || !nonNftTokens.value) return;

  try {
    const tokenAvailableBalances = await getTokenBalances(nonNftTokens.value);
    const sharesHashMap = await getShared();
    const totalEarnedHashMap = await getEarnedBalance();

    storageItems.value = nonNftTokens.value.map((token: ITokenModel, index: number): StorageItem => {
      return {
        ...token,
        balance: tokenAvailableBalances[index].toString(),
        shared: sharesHashMap[token.tokenContractAddress] || '0',
        earnedBalance: totalEarnedHashMap[token.tokenContractAddress] || '0'
      };
    });

    console.log('zzz storageItems.value ', storageItems.value);
  } catch (error) {
    console.error('zzz ERROR in getStorageItems:', error);
  }
}

watchImmediate([nonNftTokens], async () => {
  isLoadingItems.value = true;
  try {
    await getStorageItems();
  } catch (error) {
    console.error('zzz ERROR in getStorageItems:', error);
  } finally {
    isLoadingItems.value = false;
  }
});

const getNftData = async () => {
  if (!address.value) {
    nftTokens.value = [];
    return;
  }

  try {
    loadingNft.value = true;

    const erc721Tokens = Object.entries(tokensConfig)
      .filter(([, value]) => value.interface === 'erc721')
      .map(([key, value]) => ({ token: key, ...value }));

    const multicallContract = Multicall__factory.connect(blockchain.multicallAddress, getProvider());
    const erc721Interface = ERC721__factory.createInterface();
    const erc721TokensAddresses = erc721Tokens.map((tokenInfo) => {
      const tokenName = tokenInfo.token as unknown as keyof ContractAddresses;
      return (blockchain.contracts[tokenName] as ContractAddressesItem).addresses.contract;
    });

    const userTokenIds = await multicallContract.tryAggregate.staticCall(
      true,
      erc721TokensAddresses.map((nftAddress) => ({
        target: nftAddress,
        callData: erc721Interface.encodeFunctionData('getTokenIds', [address.value as any])
      }))
    );

    const userNfts = await Promise.all(
      userTokenIds.map(async (data, index) => {
        const decoded = erc721Interface.decodeFunctionResult('getTokenIds', data[1]);

        const nftAddress = erc721TokensAddresses[index];
        const userIdsForNft: number[] = decoded[0].map((i: bigint) => new BigNumber(i.toString()).toNumber());

        const mc = await multicallContract.tryAggregate.staticCall(
          true,
          userIdsForNft.map((id) => ({
            target: nftAddress,
            callData: erc721Interface.encodeFunctionData('tokenURI', [id])
          }))
        );

        return Promise.all(
          mc.map(async (tokenValue, index) => {
            const decodedImage = erc721Interface.decodeFunctionResult('tokenURI', tokenValue[1]);

            const metadata = await $fetch<NftMetadata>(decodedImage[0]).catch(() => null);

            return {
              imageUrl: metadata?.image_url || 'https://static.pearldivergame.com/output/earrings-3d3337.png',
              name: metadata?.name || 'Unknown',
              description: `${metadata?.name || 'nft'}Description`,
              tokenId: userIdsForNft[index],
              nftAddress
            };
          })
        );
      })
    );

    nftTokens.value = userNfts.flat();

    //let multicallResult: [boolean, string][] = await getMulticallResult(
    //  ERC721__factory.createInterface(),
    //  erc721TokensAddresses,
    //  'getTokenIds',
    //  true
    //);
    //
    //const erc721Ids: number[][] = multicallResult.map((elem) => getInterfaceDecoded(elem[1]));
    //
    //const tokenAddresses: [string, string][] = erc721Ids
    //  .map((tokenIdsArray: any[], tokenIndex: number) => {
    //    return tokenIdsArray[0].map((tokenId: bigint) => [
    //      erc721TokensAddresses[tokenIndex],
    //      erc721Interface.encodeFunctionData('tokenURI', [tokenId.toString()])
    //    ]);
    //  })
    //  .flat();
    //
    //multicallResult = await multicallContract?.tryAggregate.staticCall(
    //  true,
    //  tokenAddresses.map((item) => ({
    //    target: item[0],
    //    callData: item[1]
    //  }))
    //);
    //
    //const allNftUris = multicallResult.map(
    //  (multicallElem: [boolean, string]) => erc721Interface.decodeFunctionResult('tokenURI', multicallElem[1])[0]
    //);
    //
    //const nftMetadataArray: NftMetadata[] = await Promise.all(
    //  allNftUris.map((uri: string) => $fetch<NftMetadata>(uri))
    //);
    //
    //nftTokens.value = nftMetadataArray.map((nft: NftMetadata) => {
    //  const { image_url, name } = nft;
    //  return {
    //    name,
    //    imageUrl: image_url,
    //    description: `${name}Description`
    //  };
    //});
  } catch (error) {
    console.error('zzz ERROR in getNftData:', error);
    nftTokens.value = [];
  } finally {
    loadingNft.value = false;
  }
};

watch(
  [address],
  async () => {
    await getNftData();

    await refreshTokens();
  },
  {
    immediate: true
  }
);
</script>
